import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useDualGame } from '../hooks/useDualGame';
import { useKeyboardInput } from '../hooks/useKeyboardInput';
import { useSound } from '../hooks/useSound';
import { PlayerBoard } from './PlayerBoard';
import { ConfigMenu } from './ConfigMenu';
import { GameHUD } from './GameHUD';
import { DEFAULT_KEY_MAPPING } from '../types/dualGame';
import type { GameConfig, GameEvent, PlayerSide } from '../types/dualGame';
import '../styles/game.css';

const DEFAULT_CONFIG: GameConfig = {
  duration: 180,
  targetScore: 8192,
  boardSize: 4,
};

const CONFETTI_COLORS = ['#f59e0b', '#3b82f6', '#ef4444', '#10b981', '#8b5cf6', '#ec4899'];

const Confetti: React.FC = React.memo(() => {
  const pieces = useMemo(() =>
    Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: `${Math.random() * 1.5}s`,
      duration: `${1.5 + Math.random() * 2}s`,
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360,
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
    })), []);

  return (
    <div className="confetti-container">
      {pieces.map(p => (
        <div
          key={p.id}
          className="confetti-piece"
          style={{
            left: p.left,
            width: p.size,
            height: p.size * 0.6,
            backgroundColor: p.color,
            animationDelay: p.delay,
            animationDuration: p.duration,
            borderRadius: p.borderRadius,
            transform: `rotate(${p.rotation}deg)`,
          }}
        />
      ))}
    </div>
  );
});

export const DualGameBoard: React.FC = () => {
  const sound = useSound();

  const [inputFlash, setInputFlash] = useState<{ left: boolean; right: boolean }>({ left: false, right: false });
  const flashTimers = useRef<{ left: number; right: number }>({ left: 0, right: 0 });

  const onGameEvent = useCallback((event: GameEvent) => {
    switch (event.type) {
      case 'MOVE':
        if (event.merged) sound.playMerge();
        else sound.playMove();
        break;
      case 'COUNTDOWN':
        if (event.value > 0) sound.playCountdown();
        break;
      case 'TICK':
        sound.playTick();
        break;
      case 'GAME_FINISHED':
        if (event.winner === 'draw') sound.playDefeat();
        else sound.playVictory();
        break;
    }
  }, [sound]);

  const {
    startGame,
    pauseGame,
    resumeGame,
    resetGame,
    movePlayer,
    leftPlayer,
    rightPlayer,
    status,
    timeRemaining,
    countdownValue,
    winner,
  } = useDualGame(onGameEvent);

  const handleMove = useCallback((player: PlayerSide, direction: 'up' | 'down' | 'left' | 'right') => {
    movePlayer(player, direction);

    window.clearTimeout(flashTimers.current[player]);
    setInputFlash(prev => ({ ...prev, [player]: true }));
    flashTimers.current[player] = window.setTimeout(() => {
      setInputFlash(prev => ({ ...prev, [player]: false }));
    }, 120);
  }, [movePlayer]);

  const handlePause = useCallback(() => {
    if (status === 'playing') pauseGame();
    else if (status === 'paused') resumeGame();
  }, [status, pauseGame, resumeGame]);

  useKeyboardInput({
    isPlaying: status === 'playing',
    keyMapping: DEFAULT_KEY_MAPPING,
    onKeyDown: handleMove,
    onPause: handlePause,
    preventDefault: true,
  });

  const handleStart = useCallback((config: GameConfig) => {
    startGame(config);
  }, [startGame]);

  const handleReset = useCallback(() => {
    resetGame();
  }, [resetGame]);

  const handlePlayAgain = useCallback(() => {
    resetGame();
  }, [resetGame]);

  const [showGo, setShowGo] = useState(false);
  const prevCountdown = useRef(0);
  const soundRef = useRef(sound);
  useEffect(() => { soundRef.current = sound; }, [sound]);

  useEffect(() => {
    if (prevCountdown.current > 0 && countdownValue === 0 && status === 'playing') {
      soundRef.current.playGo();
      setShowGo(true);
      const t = setTimeout(() => setShowGo(false), 600);
      return () => clearTimeout(t);
    }
    prevCountdown.current = countdownValue;
  }, [countdownValue, status]);

  const leftLeading = leftPlayer.score > rightPlayer.score && leftPlayer.score > 0;
  const rightLeading = rightPlayer.score > leftPlayer.score && rightPlayer.score > 0;

  const totalScore = leftPlayer.score + rightPlayer.score;
  const leftPct = totalScore > 0 ? (leftPlayer.score / totalScore) * 100 : 50;
  const rightPct = totalScore > 0 ? (rightPlayer.score / totalScore) * 100 : 50;

  if (status === 'config') {
    return (
      <div className="dual-game-board">
        <div className="game-title">
          <h1>2048 双人对战</h1>
          <p>同键盘对战模式</p>
        </div>
        <ConfigMenu defaultConfig={DEFAULT_CONFIG} onStart={handleStart} />
        <div className="controls-hint">
          <div className="player-controls left">
            <h3>左玩家</h3>
            <div className="keys">W A S D</div>
          </div>
          <div className="vs">VS</div>
          <div className="player-controls right">
            <h3>右玩家</h3>
            <div className="keys">↑ ↓ ← →</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dual-game-board playing">
      <GameHUD
        timeRemaining={timeRemaining}
        status={status}
        winner={winner}
        leftScore={leftPlayer.score}
        rightScore={rightPlayer.score}
        onPause={handlePause}
        onReset={handleReset}
      />

      <div className="score-bar-wrapper">
        <div className="score-bar-fill left" style={{ width: `${leftPct}%` }} />
        <div className="score-bar-fill right" style={{ width: `${rightPct}%` }} />
      </div>

      <div className="game-arena">
        <div className="player-area left">
          <PlayerBoard
            player={leftPlayer}
            side="left"
            isWinner={winner === 'left'}
            isLoser={winner === 'right' || leftPlayer.isDead}
            isLeading={leftLeading && status === 'playing'}
            inputFlash={inputFlash.left}
          />
        </div>

        <div className="vs-divider">
          <div className="vs-text">VS</div>
          <div className="timer-display">
            {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
          </div>
        </div>

        <div className="player-area right">
          <PlayerBoard
            player={rightPlayer}
            side="right"
            isWinner={winner === 'right'}
            isLoser={winner === 'left' || rightPlayer.isDead}
            isLeading={rightLeading && status === 'playing'}
            inputFlash={inputFlash.right}
          />
        </div>
      </div>

      {/* Countdown overlay */}
      {status === 'countdown' && countdownValue > 0 && (
        <div className="countdown-overlay">
          <div className="countdown-number" key={countdownValue}>{countdownValue}</div>
        </div>
      )}

      {/* GO flash */}
      {showGo && (
        <div className="countdown-overlay">
          <div className="countdown-go">GO!</div>
        </div>
      )}

      {/* Paused overlay */}
      {status === 'paused' && (
        <div className="paused-overlay" onClick={handlePause}>
          <div className="paused-text">已暂停 — 按 P 继续</div>
        </div>
      )}

      {/* Game over overlay */}
      {status === 'finished' && winner && (
        <>
          {winner !== 'draw' && <Confetti />}
          <div className="game-over-overlay">
            <div className="result-panel">
              <h2 className="result-title">
                {winner === 'draw' ? '平局！' : `${winner === 'left' ? '左玩家' : '右玩家'}获胜！`}
              </h2>
              <div className="final-scores">
                <div className={`score left ${winner === 'left' ? 'winner' : ''}`}>
                  <span className="label">左玩家</span>
                  <span className="value">{leftPlayer.score}</span>
                </div>
                <div className="vs">VS</div>
                <div className={`score right ${winner === 'right' ? 'winner' : ''}`}>
                  <span className="label">右玩家</span>
                  <span className="value">{rightPlayer.score}</span>
                </div>
              </div>
              <div className="action-buttons">
                <button className="btn-primary" onClick={handlePlayAgain}>再来一局</button>
                <button className="btn-secondary" onClick={handleReset}>返回设置</button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="controls-footer">
        <div className="control-hint left">
          <span className="keys">WASD</span>
          <span className="label">左玩家</span>
        </div>
        <div className="status-hint">
          {status === 'playing' && '游戏进行中 — 按 P 暂停'}
          {status === 'paused' && '游戏已暂停 — 按 P 继续'}
        </div>
        <div className="control-hint right">
          <span className="label">右玩家</span>
          <span className="keys">↑↓←→</span>
        </div>
      </div>
    </div>
  );
};

export default DualGameBoard;
